import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import closeicon from "../../../assets/images/close.svg";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import { selectTravelFormData } from "../../../redux/features/newTravelFormSlice";
import { formatPremiumWithCurrency } from "../../../utils";
import BackdropModal from "../../common/modal/backdropModal";

const TravelSummaryModal = ({ show, onClose, selectedPlan }) => {
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();
  const isRTL = i18n.resolvedLanguage === "ar";

  // Get cover prices data from Redux store
  const formData = useSelector(selectTravelFormData);
  const coverPricesData = formData?.coverPrices || []; // Cover prices from covers API

  // Helper function to get cover price by cover code using switch case
  const getCoverPriceByCode = (coverCode) => {
    if (!coverPricesData || !Array.isArray(coverPricesData)) return 0;

    let targetCoverCode = null;
    let fallbackNames = [];

    // Switch case to match cover codes and set fallback names
    switch (coverCode) {
      case "CPER137":
        targetCoverCode = "CPER137";
        fallbackNames = [
          "Summer Activities",
          "Summer Sport",
          "Summer Activity",
        ];
        break;
      case "CPER17":
        targetCoverCode = "CPER17";
        fallbackNames = [
          "Winter Sport",
          "Water Sports",
          "Winter & Water Sports",
        ];
        break;
      default:
        console.warn(`Unknown cover code: ${coverCode}`);
        return 0;
    }

    // First try to find by exact cover code
    let cover = coverPricesData.find((c) => c.CoverCode === targetCoverCode);

    // If not found by code, try by cover name
    if (!cover) {
      cover = coverPricesData.find((c) =>
        fallbackNames.some(
          (name) =>
            c.CoverName &&
            c.CoverName.toLowerCase().includes(name.toLowerCase())
        )
      );
    }

    const price = cover?.Price || 0;
    console.log(
      `💰 Modal Cover ${coverCode}: Found price ${price} for cover:`,
      cover
    );
    return price;
  };

  // Helper functions using the switch case logic
  const getSummerCoverPrice = () => getCoverPriceByCode("CPER137");
  const getWinterCoverPrice = () => getCoverPriceByCode("CPER17");

  if (!show || !selectedPlan) return null;

  // Get quotation number directly from REAL API response - NO MORE MOCK DATA
  const quotationNumber =
    selectedPlan.apiData?.quoteNumber ||
    selectedPlan.apiData?.policyNumber ||
    selectedPlan.quoteNumber ||
    "N/A";

  // Debug logging to verify we're using real API data
  console.log("=== SUMMARY MODAL DEBUG ===");
  console.log("selectedPlan:", selectedPlan);
  console.log("Real API Quote Number:", quotationNumber);
  console.log("Is Real API Data:", selectedPlan.isRealApiData);
  console.log("Has API Data:", !!selectedPlan.apiData);
  console.log("Total Premium from API:", selectedPlan.totalPremium);
  console.log("Premium from API:", selectedPlan.premium);
  console.log("Tax from API:", selectedPlan.tax);
  console.log("Add-ons:", selectedPlan.addOns);
  console.log("Water Sport selected:", selectedPlan.addOns?.waterSport);
  console.log("Summer Activities selected:", selectedPlan.addOns?.winterSport);
  console.log("Legacy coverages:", selectedPlan.coverages);

  // Use actual API data
  const basePremium = selectedPlan.premium || 0;
  const finalTax = selectedPlan.tax || 0;
  const finalTotal = selectedPlan.totalPremium || 0;

  // Get current locale for number formatting
  const currentLocale = isRTL ? "en-US" : "en-US";

  // Helper function for RTL-aware currency formatting with 3 decimal places
  const formatCurrency = (amount) => {
    const currency = translate("BHD", "د.ب");
    const formattedNumber = formatPremiumWithCurrency(amount, "", "en-US");

    // For RTL, put number first then currency (66.000 د.ب)
    if (isRTL) {
      return (
        <span className="flex items-center gap-1 flex-row-reverse">
          <span>{currency}</span>
          <span>{formattedNumber}</span>
        </span>
      );
    }
    // For LTR, currency before number
    return `${currency} ${formattedNumber}`;
  };

  return (
    <BackdropModal show={show} onClose={onClose} hideClosebtn>
      <>
        {/* Header */}
        <div className="flex justify-between items-center mb-4 -mt-6">
          <h2 className="text-[#1A2024] text-2xl not-italic font-semibold leading-6">
            {translate("Summary", "التسعيرة")}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full max767:bg-[#E5E9EB]"
          >
            <img src={closeicon} alt="Close" className="w-6 h-6" />
          </button>
        </div>
        <div className="bg-white rounded-lg max-h-[80vh] min-w-full lg:min-w-[624px] max767:max-w-full max767:min-w-full">
          {/* Summary Details */}
          <div className="rounded-lg summary-content">
            {/* Quotation Number */}
            <div className="flex items-center justify-between bg-[#E8F4D8] px-4 py-2.5 rounded-md mb-2">
              <span className="text-[#5B6871] text-xs not-italic font-medium leading-6">
                {translate("Quotation Number", "رقم التسعيرة")}
              </span>
              <span className="text-[#5B6871] text-xs not-italic font-medium leading-6">
                {quotationNumber}
              </span>
            </div>

            {/* Premium (instead of Base Premium) */}
            <div className="flex justify-between mb-2 px-0 py-2.5">
              <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                {translate("Premium", "المبلغ")}
              </span>
              <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                {formatCurrency(basePremium)}
              </span>
            </div>

            {/* Coverage Add-ons - Show covers with prices from API */}
            {selectedPlan.apiData?.QuoteDetails?.Covers && (
              <>
                {selectedPlan.apiData.QuoteDetails.Covers.map(
                  (cover, index) => {
                    // Only show Winter and Summer Activities covers
                    if ((cover.coverCode || cover.CoverCode) === "CPER17") {
                      const coverPrice = getWinterCoverPrice();
                      console.log(
                        `🏄 Modal Water Sports: Quote API Price=${cover.Price}, Covers API Price=${coverPrice}`
                      );
                      return (
                        <div
                          key={index}
                          className="flex justify-between mb-2 px-0 py-2.5"
                        >
                          <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                            {translate("Winter Activities", "الأنشطة الشتوية")}
                          </span>
                          <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                            {formatCurrency(coverPrice)}
                          </span>
                        </div>
                      );
                    }
                    if ((cover.coverCode || cover.CoverCode) === "CPER137") {
                      const coverPrice = getSummerCoverPrice();
                      console.log(
                        `🎯 Modal Terrorism: Quote API Price=${cover.Price}, Covers API Price=${coverPrice}`
                      );
                      return (
                        <div
                          key={index}
                          className="flex justify-between mb-2 px-0 py-2.5"
                        >
                          <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                            {translate("Summer Activities", "الأنشطة الصيفية")}
                          </span>
                          <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                            {formatCurrency(coverPrice)}
                          </span>
                        </div>
                      );
                    }
                    return null;
                  }
                )}
              </>
            )}

            {/* Legacy support for old data structure when no API Covers - using covers API pricing */}
            {!selectedPlan.apiData?.QuoteDetails?.Covers && (
              <>
                {selectedPlan.addOns?.waterSport && (
                  <div className="flex justify-between mb-2 px-0 py-2.5">
                    <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                      {translate("Summer Activities", "الأنشطة الصيفية")}
                    </span>
                    <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                      {formatCurrency(getSummerCoverPrice())}
                    </span>
                  </div>
                )}

                {selectedPlan.addOns?.winterSport && (
                  <div className="flex justify-between mb-2 px-0 py-2.5">
                    <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                      {translate("Winter Activities", "الأنشطة الشتوية")}
                    </span>
                    <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                      {formatCurrency(getWinterCoverPrice())}
                    </span>
                  </div>
                )}

                {/* Legacy support for coverages format */}
                {selectedPlan.coverages?.winterActivities &&
                  !selectedPlan.addOns?.winterSport && (
                    <div className="flex justify-between mb-2 px-0 py-2.5">
                      <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                        {translate("Winter Activities", "الأنشطة الشتوية")}
                      </span>
                      <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                        {formatCurrency(getWinterCoverPrice())}
                      </span>
                    </div>
                  )}

                {selectedPlan.coverages?.summerActivities &&
                  !selectedPlan.addOns?.waterSport && (
                    <div className="flex justify-between mb-2 px-0 py-2.5">
                      <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                        {translate("Summer Activities", "الأنشطة الصيفية")}
                      </span>
                      <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                        {formatCurrency(getSummerCoverPrice())}
                      </span>
                    </div>
                  )}
              </>
            )}

            {/* VAT */}
            <div className="flex justify-between mb-2 px-0 py-2.5 border-t-[#DDE2E4] border-t border-solid">
              <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                {translate("VAT", "ضريبة القيمة المضافة")} 10%
              </span>
              <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                {formatCurrency(finalTax)}
              </span>
            </div>

            {/* Total */}
            <div className="flex justify-between px-0 py-2.5 border-t-[#DDE2E4] border-t border-solid">
              <span className="text-[#1A2024] text-base not-italic font-extrabold leading-6">
                {translate("Total", "المجموع")}
              </span>
              <span className="text-[#1A2024] text-base not-italic font-extrabold leading-6">
                {formatCurrency(finalTotal)}
              </span>
            </div>
          </div>
        </div>
      </>
    </BackdropModal>
  );
};

export default TravelSummaryModal;
